<template>
  <page-outline :title="$t('Webheader.report_builder')">
    <template slot="pageContent">
      <b-container fluid>
        <b-row class="mt-4">
          <b-col cols="12">
            <p class="builder-intro">
              Créez un rapport personnalisé : choisissez les indicateurs, appliquez les filtres, puis générez-le à la demande ou planifiez son envoi automatique aux destinataires autorisés.
            </p>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col cols="12" lg="5" class="mb-4">
            <b-card>
              <h5 class="builder-section-title">1. {{ $t('mandate.report_builder.emitter') }}</h5>
              <select v-model="selectedEmitter" class="filter-item-content w-100 mb-4" style="padding:0.35rem">
                <option value="Tous">{{ $t('mandate.report_builder.all_emitters_consolidated') }}</option>
                <option v-for="e in entityOptions" :key="e" :value="e">{{ e }}</option>
              </select>
              <p class="builder-hint">
                {{ $t('mandate.report_builder.emitter_access_hint') }}
              </p>

              <hr class="my-4" />

              <h5 class="builder-section-title">2. {{ $t('mandate.report_builder.indicators_columns') }}</h5>
              <div class="indicator-grid">
                <b-form-checkbox
                  v-for="ind in availableIndicators"
                  :key="ind.key"
                  v-model="selectedIndicators"
                  :value="ind.key"
                  class="mb-2"
                >
                  {{ ind.label }}
                </b-form-checkbox>
              </div>

              <hr class="my-4" />

              <h5 class="builder-section-title">3. {{ $t('mandate.report_builder.filters') }}</h5>
              <b-row>
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.start_date') }}</span>
                  <b-form-input v-model="dateBeginFilter" type="date" size="sm" />
                </b-col>
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.end_date') }}</span>
                  <b-form-input v-model="dateEndFilter" type="date" size="sm" />
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.status') }}</span>
                  <select v-model="statusFilter" class="filter-item-content w-100" style="padding:0.35rem">
                    <option value="Tous">{{ $t('mandate.report_builder.all_statuses') }}</option>
                    <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
                  </select>
                </b-col>
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.group_by') }}</span>
                  <select v-model="groupBy" class="filter-item-content w-100" style="padding:0.35rem">
                    <option value="emitter">{{ $t('mandate.report_builder.emitter') }}</option>
                    <option value="month">{{ $t('mandate.report_builder.month') }}</option>
                    <option value="status">{{ $t('mandate.report_builder.status') }}</option>
                  </select>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="12">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.sort_by') }}</span>
                  <select v-model="sortBy" class="filter-item-content w-100" style="padding:0.35rem">
                    <option value="date_desc">{{ $t('mandate.report_builder.date_newest_first') }}</option>
                    <option value="date_asc">{{ $t('mandate.report_builder.date_oldest_first') }}</option>
                    <option value="amount_desc">{{ $t('mandate.report_builder.amount_highest_first') }}</option>
                  </select>
                </b-col>
              </b-row>

              <hr class="my-4" />

              <h5 class="builder-section-title">4. {{ $t('mandate.report_builder.scheduling_distribution') }}</h5>
              <b-row>
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.frequency') }}</span>
                  <select v-model="scheduleFrequency" class="filter-item-content w-100" style="padding:0.35rem">
                    <option value="on_demand">{{ $t('mandate.report_builder.on_demand_only') }}</option>
                    <option value="Quotidien">{{ $t('mandate.report_builder.daily') }}</option>
                    <option value="Hebdomadaire">{{ $t('mandate.report_builder.weekly') }}</option>
                    <option value="Mensuel">{{ $t('mandate.report_builder.monthly') }}</option>
                  </select>
                </b-col>
                <b-col cols="6">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.format') }}</span>
                  <select v-model="exportFormat" class="filter-item-content w-100" style="padding:0.35rem">
                    <option value="csv">CSV</option>
                    <option value="excel">Excel</option>
                    <option value="pdf">PDF</option>
                  </select>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="12">
                  <span class="filter-item-title d-block mb-1">{{ $t('mandate.report_builder.recipients_comma_separated') }}</span>
                  <b-form-input v-model="recipients" :placeholder="$t('mandate.report_builder.recipients_placeholder')" size="sm" />
                </b-col>
              </b-row>

              <b-row class="mt-4">
                <b-col cols="12" class="d-flex justify-content-between align-items-center flex-wrap">
                  <span class="next-run-info" v-if="scheduleFrequency !== 'on_demand'">
                    {{ $t('mandate.report_builder.next_scheduled_run') }}: <strong>{{ nextRunLabel }}</strong>
                  </span>
                  <span class="next-run-info" v-else>{{ $t('mandate.report_builder.no_schedule_set') }}</span>
                  <b-button class="btnFilter_2" @click="generateReport">
                    <i class="fa-light fa-play"></i>
                    {{ $t('mandate.report_builder.generate_now') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col cols="12" lg="7">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h5 class="builder-section-title mb-0">{{ $t('mandate.report_builder.live_preview') }}</h5>
                <b-badge variant="secondary">{{ selectedEmitter === 'Tous' ? $t('mandate.report_builder.all_emitters') : selectedEmitter }}</b-badge>
              </div>

              <div v-if="selectedIndicators.length === 0" class="d-flex justify-content-center py-5">
                <span class="noResult">{{ $t('mandate.report_builder.select_indicator_preview') }}</span>
              </div>

              <div v-else class="table-responsive">
                <b-table
                  striped hover small
                  :items="previewItems"
                  :fields="previewFields"
                  class="mb-0"
                ></b-table>
              </div>

              <b-row class="mt-4">
                <b-col cols="12" class="d-flex justify-content-end">
                  <b-button class="btnFilter_2" @click="exportPreview" :disabled="selectedIndicators.length === 0">
                    <i class="fa-light fa-upload"></i>
                    {{ $t('mandate.report_builder.export_preview') }} ({{ exportFormat.toUpperCase() }})
                  </b-button>
                </b-col>
              </b-row>
            </b-card>

            <b-card class="mt-4" v-if="savedTemplates.length">
              <h5 class="builder-section-title">{{ $t('mandate.report_builder.saved_templates_by_emitter') }}</h5>
              <div class="table-responsive">
                <b-table
                  striped hover small
                  :items="savedTemplates"
                  :fields="savedTemplatesFields"
                  class="mb-0"
                >
                  <template #cell(frequency)="data">
                    <b-badge variant="info">{{ data.value }}</b-badge>
                  </template>
                </b-table>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import moment from 'moment-timezone'

export default {
  name: 'ReportBuilder',
  components: {
    'page-outline': PageOutline
  },
  data () {
    return {

      entityOptions: [
        'SNCF Voyageurs',
        'SNCF Reseau',
        'SNCF Gares & Connexions',
        'SNCF Immobilier',
        'Rail Logistics Europe'
      ],

      statusOptions: [
        { value: 'Valid mandate', label: this.$t('mandate.report_builder.valid_mandate') },
        { value: 'Sent to PSP Debtor', label: this.$t('mandate.report_builder.sent_to_psp_debtor') },
        { value: 'Created / Pending signature', label: this.$t('mandate.report_builder.created_pending_signature') },
        { value: 'Rejected', label: this.$t('mandate.report_builder.rejected') }
      ],
      availableIndicators: [
        { key: 'mandateCount', label: this.$t('mandate.report_builder.mandate_count') },
        { key: 'collectionCount', label: this.$t('mandate.report_builder.collection_count') },
        { key: 'totalAmount', label: this.$t('mandate.report_builder.total_amount') },
        { key: 'successRate', label: this.$t('mandate.report_builder.success_rate') },
        { key: 'rejectRate', label: this.$t('mandate.report_builder.reject_rate') },
        { key: 'topRejectMotif', label: this.$t('mandate.report_builder.top_reject_motif') },
        { key: 'recoveryRate', label: this.$t('mandate.report_builder.recovery_rate') },
        { key: 'avgProcessingTime', label: this.$t('mandate.report_builder.average_processing_time') }
      ],
      selectedEmitter: 'Tous',
      selectedIndicators: ['mandateCount', 'totalAmount', 'successRate'],
      dateBeginFilter: '',
      dateEndFilter: '',
      statusFilter: 'Tous',
      groupBy: 'emitter',
      sortBy: 'date_desc',
      scheduleFrequency: 'Mensuel',
      exportFormat: 'csv',
      recipients: '',
      emitterDataset: [
        { emitter: 'SNCF Voyageurs', mandateCount: 542, collectionCount: 1310, totalAmount: 128450, successRate: 96.4, rejectRate: 3.6, topRejectMotif: 'AM04', recoveryRate: 72.6, avgProcessingTime: 480 },
        { emitter: 'SNCF Reseau', mandateCount: 118, collectionCount: 340, totalAmount: 42300, successRate: 94.1, rejectRate: 5.9, topRejectMotif: 'MS02', recoveryRate: 68.2, avgProcessingTime: 510 },
        { emitter: 'SNCF Gares & Connexions', mandateCount: 96, collectionCount: 210, totalAmount: 18750, successRate: 97.2, rejectRate: 2.8, topRejectMotif: 'MD01', recoveryRate: 75.4, avgProcessingTime: 460 },
        { emitter: 'SNCF Immobilier', mandateCount: 41, collectionCount: 88, totalAmount: 9600, successRate: 98.0, rejectRate: 2.0, topRejectMotif: 'AC06', recoveryRate: 80.1, avgProcessingTime: 430 },
        { emitter: 'Rail Logistics Europe', mandateCount: 63, collectionCount: 150, totalAmount: 21980, successRate: 95.6, rejectRate: 4.4, topRejectMotif: 'MD07', recoveryRate: 70.9, avgProcessingTime: 495 }
      ],
      savedTemplates: [
        { name: 'Synthèse mensuelle - SNCF Voyageurs', emitter: 'SNCF Voyageurs', frequency: 'Mensuel', lastRun: '2026-07-01', recipients: 'ops.voyageurs@sncf.fr' },
        { name: 'Suivi hebdomadaire des rejets - SNCF Reseau', emitter: 'SNCF Reseau', frequency: 'Hebdomadaire', lastRun: '2026-07-07', recipients: 'reporting@sncf.fr' },
        { name: 'Tableau de bord consolidé – Tous les émetteurs', emitter: 'Tous', frequency: 'Quotidien', lastRun: '2026-07-13', recipients: 'pilotage.sdd@sncf.fr' }
      ]
    }
  },
  computed: {
    indicatorLabelMap () {
      const map = {}
      this.availableIndicators.forEach(i => { map[i.key] = i.label })
      return map
    },
    filteredDataset () {
      if (this.selectedEmitter === 'Tous') return this.emitterDataset
      return this.emitterDataset.filter(row => row.emitter === this.selectedEmitter)
    },
    previewFields () {
      const fields = [{ key: 'emitter', label: this.$t('mandate.report_builder.emitter'), sortable: true }]
      this.selectedIndicators.forEach(key => {
        fields.push({ key, label: this.indicatorLabelMap[key], sortable: true })
      })
      return fields
    },
    previewItems () {
      return this.filteredDataset.map(row => {
        const item = { emitter: row.emitter }
        this.selectedIndicators.forEach(key => {
          let value = row[key]
          if (['successRate', 'rejectRate', 'recoveryRate'].includes(key)) value = value.toFixed(1) + ' %'
          if (key === 'totalAmount') value = value.toLocaleString('en-US', { style: 'currency', currency: 'EUR' })
          if (key === 'avgProcessingTime') value = value + ' ms'
          item[key] = value
        })
        return item
      })
    },
    savedTemplatesFields () {
      return [
        { key: 'name', label: this.$t('mandate.report_builder.template_name'), sortable: true },
        { key: 'emitter', label: this.$t('mandate.report_builder.emitter'), sortable: true },
        { key: 'frequency', label: this.$t('mandate.report_builder.frequency'), sortable: true },
        { key: 'lastRun', label: this.$t('mandate.report_builder.last_run'), sortable: true },
        { key: 'recipients', label: this.$t('mandate.report_builder.recipients'), sortable: true }
      ]
    },
    nextRunLabel () {
      const today = moment()
      if (this.scheduleFrequency === 'Quotidien') return today.add(1, 'day').format('YYYY-MM-DD')
      if (this.scheduleFrequency === 'Hebdomadaire') return today.add(1, 'week').format('YYYY-MM-DD')
      if (this.scheduleFrequency === 'Mensuel') return today.add(1, 'month').startOf('month').format('YYYY-MM-DD')
      return '-'
    }
  },
  methods: {
    generateReport () {
      // a brancher sur le vrai envoi mail planifie
      this.exportPreview()
    },
    exportPreview () {
      const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`
      const headers = this.previewFields.map(f => f.label)
      const rows = this.previewItems.map(item => this.previewFields.map(f => item[f.key]))
      const csv = [headers, ...rows].map(r => r.map(esc).join(';')).join('\r\n')

      const linkSource = 'data:text/csv;charset=utf-8;base64,' + btoa(csv)
      const fileName = 'custom_report_' + this.selectedEmitter.replace(/\s+/g, '_') + '_' + moment().unix() + '.csv'

      const a = document.createElement('a')
      a.href = linkSource
      a.download = fileName
      a.click()
    }
  }
}
</script>

<style scoped>
.builder-intro {
  color: #6b7280;
  font-size: 0.9rem;
}

.builder-section-title {
  color: var(--actionButtonColorBlue);
  font-weight: 600;
}

.builder-hint {
  color: #6b7280;
  font-size: 0.8rem;
  margin-bottom: 0;
}

.indicator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem 1rem;
}

.next-run-info {
  color: #6b7280;
  font-size: 0.85rem;
}

::v-deep .table-responsive table thead th {
  text-align: center;
  vertical-align: middle;
}

::v-deep .table-responsive table tbody td {
  text-align: center;
  vertical-align: middle;
}

@media (max-width: 767px) {
  .indicator-grid {
    grid-template-columns: 1fr;
  }
}
</style>
